import Upcoming from './Upcoming';
import { useQuery } from '@tanstack/react-query';
import { getUserInfo } from 'api/requests/core/users';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { queryKeyBuilder } from 'shared/query';
import { hasAnyOfRole } from 'shared/utils/auth';

type Props = {
  icon: (props: React.SVGAttributes<SVGElement>) => JSX.Element;
  label: string;
  isActive?: () => boolean;
  to: string;
  featureFlag?: string;
  rolesRequired?: Array<string>;
  isUpcoming?: boolean;
  isDisabled?: boolean;
  onClose?: () => void;
};

function Entry({ icon, label, isActive, to, rolesRequired, isUpcoming = false, isDisabled = false, onClose }: Props) {
  const Icon = icon;
  const navigate = useNavigate();
  const location = useLocation();

  const isPathActive = isActive ? isActive() : location.pathname === to;
  const disabled = isDisabled || isUpcoming;

  const { data: userData } = useQuery({
    queryKey: queryKeyBuilder.currentUser(),
    queryFn: getUserInfo,
  });

  const requirePermission = Array.isArray(rolesRequired) ? rolesRequired.length > 0 : !!rolesRequired;
  const hasPermission = !requirePermission || hasAnyOfRole(userData, rolesRequired || []);

  return hasPermission ? (
    <button
      disabled={disabled}
      onClick={() => {
        navigate(to);
        onClose?.();
      }}
      className={`relative group h-[44px] rounded w-full flex items-center gap-2.5 py-3 px-4 transition-colors disabled:bg-palette-sbiu-grey ${
        isPathActive ? 'bg-palette-gold' : 'bg-palette-sbiu-grey hover:bg-palette-pinkish-gray'
      }`}
    >
      <Icon
        className={`transition-colors w-[14px] h-[14px] ${
          disabled ? 'text-palette-color-border' : isPathActive ? 'text-white' : 'text-black'
        }`}
      />
      <span
        className={`tracking-tight transition-all truncate whitespace-nowrap ${
          disabled
            ? 'text-palette-color-border'
            : isPathActive
            ? 'text-white font-semibold'
            : 'text-black group-hover:font-semibold font-normal'
        }`}
      >
        {label}
      </span>
      {isUpcoming && <Upcoming />}
    </button>
  ) : null;
}

export default Entry;
